import React, { useState } from "react";
import { ShareLink } from "@secure-caseworker/shared";
import Badge from "../common/ui/badge/Badge";
import Button from "../common/ui/button/Button";
import { Copy, ExternalLink, XCircle } from "lucide-react";
import { Table, TableColumn } from "../common/tables/Table";

interface ShareLinkManagerProps {
  shareLinks: ShareLink[];
  onRevoke: (shareLinkId: string) => Promise<void>;
}

export const ShareLinkManager: React.FC<ShareLinkManagerProps> = ({
  shareLinks,
  onRevoke,
}) => {
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const copyToClipboard = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyingId(id);
      setTimeout(() => setCopyingId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleRevoke = async (id: string) => {
    if (
      !confirm(
        "Are you sure you want to revoke this share link? This cannot be undone."
      )
    ) {
      return;
    }

    setRevokingId(id);
    try {
      await onRevoke(id);
    } catch (err) {
      console.error("Failed to revoke:", err);
    } finally {
      setRevokingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: "success" as const, label: "Active" },
      expired: { color: "light" as const, label: "Expired" },
      revoked: { color: "error" as const, label: "Revoked" },
      used: { color: "warning" as const, label: "Used" },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.active;

    return (
      <Badge color={config.color} variant="solid" size="sm">
        {config.label}
      </Badge>
    );
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleString();
  };

  const getUsageText = (link: ShareLink) => {
    if (link.maxDownloads === null) {
      return `${link.downloadCount} / unlimited`;
    }
    return `${link.downloadCount} / ${link.maxDownloads}`;
  };

  const columns: TableColumn[] = [
    {
      header: "Status",
      accessor: "status",
      render: (value) => getStatusBadge(value || "active"),
    },
    {
      header: "Created",
      accessor: "createdAt",
      render: (value) => formatDate(value),
    },
    {
      header: "Expires",
      accessor: "expiresAt",
      render: (value) => formatDate(value),
    },
    {
      header: "Usage",
      accessor: "id",
      render: (_, link: ShareLink) => getUsageText(link),
    },
    {
      header: "Link",
      accessor: "publicUrl",
      render: (value, link: ShareLink) => (
        <div className="flex items-center gap-2 max-w-xs">
          <code className="flex-1 truncate text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded font-mono">
            {value || `.../${link.token.substring(0, 8)}...`}
          </code>
        </div>
      ),
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_, link: ShareLink) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyToClipboard(link.publicUrl || "", link.id)}
            startIcon={<Copy className="h-4 w-4" />}
            disabled={link.status !== "active"}
          >
            {copyingId === link.id ? "Copied!" : "Copy"}
          </Button>
          {link.status === "active" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRevoke(link.id)}
              startIcon={<XCircle className="h-4 w-4" />}
              disabled={revokingId === link.id}
            >
              {revokingId === link.id ? "Revoking..." : "Revoke"}
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (shareLinks.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
        <ExternalLink className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No share links created yet
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Create your first share link to share this resource
        </p>
      </div>
    );
  }

  return <Table columns={columns} data={shareLinks} />;
};
